import { forwardRef, useEffect, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { isHtmlContent } from 'src/lib/html';
import { EMPTY_PARAGRAPH_HTML, htmlToSlate, replaceSlateEditorHtml } from 'src/lib/slate';

import { CustomEditable } from '../../components/CustomEditable';
import { CustomSlate } from '../../components/CustomSlate';
import { useRichSlateEditor } from '../../useRichSlateEditor';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  html: PropTypes.string,
  placeholder: PropTypes.string,
};
const defaultProps = {
  className: '',
  classes: null,
  html: '',
  placeholder: '',
};

export const ReadonlyHtmlEditor = forwardRef(
  ({ className, classes, html, placeholder, ...restProps }, ref) => {
    const styles = useStyles({ classes });

    const editor = useRichSlateEditor();
    const [initialValue] = useState(() =>
      htmlToSlate(html ? wrapInlineContentInParagraph(html) : EMPTY_PARAGRAPH_HTML)
    );

    useEffect(() => {
      replaceSlateEditorHtml(editor, wrapInlineContentInParagraph(html));
    }, [editor, html]);

    return (
      <div
        className={cx(styles.root, className)}
        ref={ref}
        {...restProps}
      >
        <CustomSlate
          editor={editor}
          initialValue={initialValue}
        >
          <CustomEditable
            className={styles.editable}
            placeholder={placeholder}
            readOnly
          />
        </CustomSlate>
      </div>
    );
  }
);

ReadonlyHtmlEditor.displayName = 'ReadonlyHtmlEditor';
ReadonlyHtmlEditor.propTypes = propTypes;
ReadonlyHtmlEditor.defaultProps = defaultProps;

// Plain text and span-only HTML both come out of `htmlToSlate` as bare text with no block around
// it, which the editor does not render.
const wrapInlineContentInParagraph = (html) => {
  // Trim spaces/newlines
  const trimmed = html.trim();

  if (trimmed && !isHtmlContent(trimmed)) {
    return `<p>${trimmed}</p>`;
  }

  // Regex: does the string consist of only spans (possibly with whitespace in between)?
  const onlySpans = /^(\s*<span\b[^>]*>[\s\S]*?<\/span>\s*)+$/.test(trimmed);

  if (onlySpans) {
    return `<p>${trimmed}</p>`;
  }
  return html;
};
